<template>
  <MtxPage width="sm" class="account">
    <template #header v-if="!user.ethAddress">
      <h1>Please sign in to view your account</h1>
    </template>

    <template v-else>
      <div class="text-center mb-3">
        <h1>Your Account</h1>
        <div class="inline-flex align-center mb-2">
          <MtxBlockie :input="user.ethAddress" :options="{ scale: 12 }" class="mr-2" />
          <div>
            <h3 class="mb-0">
              {{ user.ethAddress | ethAddress }}
              <Copy plain :text="user.ethAddress" />
            </h3>
            <div class="flex" v-if="web3.mtxBalance !== null">
              <p class="mb-0 mr-1 heading-2">{{ web3.mtxBalance | mtx }}</p>
              <button class="btn--link heading-3 inline-flex" @click="watchToken">
                <!-- TODO: change icon to MTX -->
                <MtxSvg icon="icon-coin" />
              </button>
            </div>
          </div>
        </div>
        <div>
          <RouterLink :to="`/profile/${user.ethAddress}`" class="btn">
            View Your Profile
          </RouterLink>
        </div>
      </div>

      <MtxCard>
        <form @submit.prevent>
          <div class="grid grid-2-columns grid-gap">
            <MtxInput
              v-model="localUser.firstName"
              label="First Name"
              :validation="$v.localUser.firstName"
              class="mb-2"
            />

            <MtxInput
              v-model="localUser.lastName"
              label="Last Name"
              :validation="$v.localUser.lastName"
              class="mb-2"
            />
          </div>

          <MtxInput
            v-model="localUser.email"
            label="Email"
            :validation="$v.localUser.email"
            class="mb-3"
            description="Where you want to be notified about changes to your tournaments and submissions."
          />

          <fieldset>
            <legend class="heading-6">Notifications:</legend>
            <div class="account__notification grid column-gap align-center mb-2">
              Tournament Updates
              <MtxInput
                v-model="localUser.tournamentUpdates"
                type="switch"
                label="Tournament Updates"
              />
            </div>
            <div class="account__notification grid column-gap align-center mb-2">
              Submission Updates
              <MtxInput
                v-model="localUser.submissionUpdates"
                type="switch"
                label="Submission Updates"
              />
            </div>
            <div class="account__notification grid column-gap align-center mb-2">
              Commit Updates
              <MtxInput v-model="localUser.commitUpdates" type="switch" label="Commit Updates" />
            </div>
          </fieldset>

          <button
            :class="{ 'btn--disabled': $v.$invalid || !hasChanges || isLoading }"
            @click="onSaveProfile"
          >
            Save Changes
          </button>
        </form>
      </MtxCard>

      <MyRewards />
      <MyTournaments />
      <MySubmissions />
      <MyCommits />
    </template>
  </MtxPage>
</template>

<script>
import isEqual from "lodash.isequal"
import { mapState, mapActions } from "vuex"
import { email, maxLength, required } from "@/utils/validators"
import errorLogger from "@/utils/errorLogger"
import errors from "@/data/errors"
import MyTournaments from "@/components/account/MyTournaments"
import MyCommits from "@/components/account/MyCommits"
import MyRewards from "@/components/account/MyRewards"
import MySubmissions from "@/components/account/MySubmissions"
import web3 from "@/mixins/web3"

export default {
  mixins: [web3],

  components: {
    MyTournaments,
    MyCommits,
    MyRewards,
    MySubmissions
  },

  data: () => ({
    isLoading: false,
    localUser: {}
  }),

  metaInfo: {
    titleTemplate: "%s | Your Account"
  },

  computed: {
    ...mapState(["user"]),

    hasChanges() {
      return !isEqual(this.localUser, {
        ...this.user,
        email: this.user.email === null ? "" : this.user.email // In case user has not setup account yet
      })
    }
  },

  watch: {
    user: {
      handler: "cloneUser",
      deep: true,
      immediate: true
    }
  },

  methods: {
    ...mapActions(["editUser"]),

    cloneUser() {
      this.localUser = {
        ...this.user
      }
    },

    async onSaveProfile() {
      try {
        this.$v.$touch()
        if (this.$v.$invalid || !this.hasChanges || this.isLoading) return

        this.isLoading = true
        const { success } = await this.editUser(this.localUser)
        if (success) {
          this.cloneUser()

          this.$notify({
            type: "success",
            text: "Saved"
          })
        }
      } catch (error) {
        errorLogger.log(error)
        this.$notify({
          title: "Error",
          type: "error",
          text: errors.API_UNKNOWN
        })
      } finally {
        this.isLoading = false
      }
    },

    async watchToken() {
      const Token = await this.contracts.Token
      const provider = window.web3.currentProvider

      provider.sendAsync(
        {
          method: "metamask_watchAsset",
          params: {
            type: "ERC20",
            options: {
              address: Token.address,
              symbol: "MTX",
              decimals: 18
              // image: tokenImage // TODO
            }
          },
          id: Date.now()
        },
        () => {} // noop
      )
    }
  },

  validations: {
    localUser: {
      firstName: {
        maxLength: maxLength(50),
        required
      },
      lastName: {
        maxLength: maxLength(50),
        required
      },
      email: {
        email,
        required
      }
    }
  }
}
</script>

<style lang="scss">
.account {
  &__notification {
    grid-template-columns: 1fr auto;
  }
}
</style>
